import { useState } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@components/ui/table";
import { Button } from "@components/ui/button";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useClientesFinalesMetrics } from "@/hooks/analytics/useClientesFinalesMetrics";
import { useIntentosPorClienteFinal } from "@/hooks/analytics/useIntentosPorClienteFinal";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { Skeleton } from "@components/ui/Skeleton";
import { IntentosTable } from "@/components/intentos/IntentosTable";
import { PanelFilters } from "@/components/panel-clientes-finales/PanelFilters";

export default function PanelClientesFinales() {
  const { usuarioInfo, loadingUsuarioInfo } = useCurrentUser();

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<{
    clienteFinalId: number | null;
    programaId: number | null;
    actividadId: number | null;
    dateFrom: string | null;
    dateTo: string | null;
  }>({
    clienteFinalId: null,
    programaId: null,
    actividadId: null,
    dateFrom: null,
    dateTo: null,
  });

  const clienteIntermedioId = usuarioInfo?.clientesIntermedios?.[0]?.id ?? null;

  const { data, isLoading, isFetching } = useClientesFinalesMetrics({
    clienteIntermedioId: clienteIntermedioId ?? undefined,
    clienteFinalId: filters.clienteFinalId ?? undefined,
    programaId: filters.programaId ?? undefined,
    actividadId: filters.actividadId ?? undefined,
    dateFrom: filters.dateFrom ?? undefined,
    dateTo: filters.dateTo ?? undefined,
  });

  const toggleRow = (clienteFinalId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(clienteFinalId)) {
      newExpanded.delete(clienteFinalId);
    } else {
      newExpanded.add(clienteFinalId);
    }
    setExpandedRows(newExpanded);
  };

  if (loadingUsuarioInfo || isLoading) {
    return (
      <div className="space-y-2 m-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-80" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!clienteIntermedioId) {
    return (
      <div className="text-sm text-muted-foreground">
        No encontramos información del cliente intermedio asociado a tu cuenta.
      </div>
    );
  }

  return (
    <section
      className="w-full p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6"
      style={{ overflowX: "hidden" }}
    >
      <PanelFilters
        filters={filters}
        onFiltersChange={setFilters}
        clienteIntermedioId={clienteIntermedioId}
      />
      <div
        className="rounded-md border w-full overflow-hidden"
        style={{ maxWidth: "100%", boxSizing: "border-box" }}
      >
        <Table
          className="w-full table-fixed"
          style={{ maxWidth: "100%", tableLayout: "fixed" }}
        >
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead className="w-[250px]">Cliente Final</TableHead>
              <TableHead className="w-[140px]">Total Entregas</TableHead>
              <TableHead className="w-[160px]">Interacciones IA</TableHead>
              <TableHead className="w-[180px]">Tiempo Promedio IA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isFetching && data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6">
                  No hay clientes finales disponibles.
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((cliente) => {
                const isExpanded = expandedRows.has(cliente.clienteFinalId);
                return (
                  <>
                    <TableRow key={cliente.clienteFinalId}>
                      <TableCell>
                        <Button
                          variant="icon"
                          size="sm"
                          onClick={() => toggleRow(cliente.clienteFinalId)}
                          className="h-8 w-8 p-0"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell
                        className="font-medium truncate"
                        title={cliente.clienteFinalNombre}
                      >
                        {cliente.clienteFinalNombre}
                      </TableCell>
                      <TableCell className="truncate">
                        {cliente.totalEntregas.toLocaleString()}
                      </TableCell>
                      <TableCell className="truncate">
                        {cliente.totalInteraccionesIA.toLocaleString()}
                      </TableCell>
                      <TableCell className="truncate">
                        {cliente.tiempoPromedioRespuestaIA === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          `${cliente.tiempoPromedioRespuestaIA.toFixed(2)}s`
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="bg-muted/50 p-0 max-w-full overflow-hidden"
                        >
                          <IntentosDetalleRow
                            clienteFinalId={cliente.clienteFinalId}
                            filters={filters}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function IntentosDetalleRow({
  clienteFinalId,
  filters,
}: {
  clienteFinalId: number;
  filters: {
    clienteFinalId: number | null;
    programaId: number | null;
    actividadId: number | null;
    dateFrom: string | null;
    dateTo: string | null;
  };
}) {
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading } = useIntentosPorClienteFinal(clienteFinalId, {
    page,
    limit,
    dateFrom: filters.dateFrom ?? undefined,
    dateTo: filters.dateTo ?? undefined,
    programaId: filters.programaId ?? undefined,
    actividadId: filters.actividadId ?? undefined,
  });

  return (
    <div
      className="p-3 sm:p-4 md:p-6 w-full overflow-hidden"
      style={{ maxWidth: "100%", boxSizing: "border-box" }}
    >
      <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
        Intentos por Aprendiz y Actividad
      </h3>
      <div
        className="w-full overflow-hidden"
        style={{ maxWidth: "100%", boxSizing: "border-box" }}
      >
        <IntentosTable intentos={data?.data ?? []} isLoading={isLoading} />
      </div>
      {data && data.pagination.total > limit && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0 mt-3 sm:mt-4">
          <span className="text-xs sm:text-sm text-muted-foreground">
            Página {page} de {Math.ceil(data.pagination.total / limit)} (
            {data.pagination.total} total)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="text-xs sm:text-sm"
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(data.pagination.total / limit)}
              className="text-xs sm:text-sm"
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
